if (!customElements.get('product-details')) {
  customElements.define(
    'product-details',
    class ProductDetails extends HTMLElement {
      connectedCallback() {
        this.description = this.querySelector('.product-details__description');
        this.descriptionContent = this.querySelector('[data-description-content]');
        this.descriptionToggle = this.querySelector('[data-description-toggle]');
        this.specificationAccordions = Array.from(
          this.querySelectorAll('.product-details__specifications details')
        );
        this.faqToggle = this.querySelector('[data-faq-toggle]');
        this.extraFaqs = Array.from(this.querySelectorAll('[data-faq-extra]'));
        this.specificationsColumn = this.querySelector('.product-details__specifications-column');
        this.gallery = this.querySelector('.product-details__gallery-list');
        this.galleryScrollIndicator = this.querySelector('.product-details__gallery-scroll-indicator');
        this.galleryScrollThumb = this.querySelector('.product-details__gallery-scroll-thumb');
        this.descriptionExpanded = false;
        this.faqExpanded = false;
        this.resizeFrame = null;
        this.galleryScrollFrame = null;
        this.galleryScrollController = null;
        this.galleryScrollObserver = null;
        this.descriptionHeightObserver = null;

        this.onDescriptionToggle = this.toggleDescription.bind(this);
        this.onSpecificationToggle = this.scheduleDescriptionRefresh.bind(this);
        this.onFaqToggle = this.toggleFaqs.bind(this);
        this.onWindowResize = this.scheduleDescriptionRefresh.bind(this);

        this.descriptionToggle?.addEventListener('click', this.onDescriptionToggle);
        this.specificationAccordions.forEach((accordion) => {
          accordion.addEventListener('toggle', this.onSpecificationToggle);
        });
        this.faqToggle?.addEventListener('click', this.onFaqToggle);
        window.addEventListener('resize', this.onWindowResize);

        this.descriptionContent?.querySelectorAll('img').forEach((image) => {
          image.addEventListener('load', this.onWindowResize);
        });

        this.setupFaqs();
        this.setupDescriptionHeightObserver();
        this.setupGalleryScrollIndicator();
        this.scheduleDescriptionRefresh();
      }

      disconnectedCallback() {
        this.descriptionToggle?.removeEventListener('click', this.onDescriptionToggle);
        this.specificationAccordions.forEach((accordion) => {
          accordion.removeEventListener('toggle', this.onSpecificationToggle);
        });
        this.faqToggle?.removeEventListener('click', this.onFaqToggle);
        window.removeEventListener('resize', this.onWindowResize);

        this.galleryScrollController?.abort();
        this.galleryScrollObserver?.disconnect();
        this.descriptionHeightObserver?.disconnect();

        this.descriptionContent?.querySelectorAll('img').forEach((image) => {
          image.removeEventListener('load', this.onWindowResize);
        });

        if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
        if (this.galleryScrollFrame) window.cancelAnimationFrame(this.galleryScrollFrame);
      }

      scheduleDescriptionRefresh() {
        if (!this.description || !this.descriptionContent || !this.descriptionToggle) return;
        if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);

        this.resizeFrame = window.requestAnimationFrame(() => {
          this.refreshDescription();
          this.resizeFrame = null;
        });
      }

      refreshDescription() {
        const isDesktop = window.matchMedia('(min-width: 990px)').matches;

        if (!isDesktop || !this.specificationsColumn) {
          this.description.style.removeProperty('height');
          this.descriptionContent.style.removeProperty('max-height');

          if (this.descriptionExpanded) {
            this.description.classList.remove('is-collapsed');
            this.descriptionToggle.hidden = false;
            this.updateToggle(this.descriptionToggle, true);
            return;
          }

          this.description.classList.add('is-collapsed');
          const hasOverflow = this.descriptionContent.scrollHeight > this.descriptionContent.clientHeight + 1;
          this.descriptionToggle.hidden = !hasOverflow;
          this.updateToggle(this.descriptionToggle, false);
          return;
        }

        this.description.classList.remove('is-collapsed');
        this.description.style.removeProperty('height');
        this.descriptionContent.style.removeProperty('max-height');
        this.descriptionToggle.hidden = false;

        const descriptionStyles = window.getComputedStyle(this.description);
        const verticalPadding =
          parseFloat(descriptionStyles.paddingTop) + parseFloat(descriptionStyles.paddingBottom);
        const toggleStyles = window.getComputedStyle(this.descriptionToggle);
        const toggleMarginTop = parseFloat(toggleStyles.marginTop);
        const fullContentHeight = this.descriptionContent.scrollHeight;
        const toggleHeight = this.descriptionToggle.getBoundingClientRect().height;
        const toggleSpacing = toggleMarginTop + toggleHeight;
        const naturalDescriptionHeight = verticalPadding + fullContentHeight + toggleSpacing;

        if (this.descriptionExpanded) {
          this.description.style.height = `${naturalDescriptionHeight}px`;
          this.updateToggle(this.descriptionToggle, true);
          return;
        }

        const previousDisplay = this.description.style.display;
        this.description.style.display = 'none';
        const specificationsHeight = this.specificationsColumn.getBoundingClientRect().height;
        this.description.style.display = previousDisplay;
        if (specificationsHeight <= 0) return;

        const shouldCollapse = naturalDescriptionHeight > specificationsHeight + 1;
        const descriptionHeight = shouldCollapse ? specificationsHeight : naturalDescriptionHeight;
        this.description.style.height = `${descriptionHeight}px`;

        if (!shouldCollapse) {
          this.descriptionToggle.hidden = true;
          this.updateToggle(this.descriptionToggle, false);
          return;
        }

        this.description.classList.add('is-collapsed');
        this.descriptionContent.style.maxHeight = `${Math.max(
          0,
          descriptionHeight - verticalPadding - toggleSpacing
        )}px`;
        this.descriptionToggle.hidden = false;
        this.updateToggle(this.descriptionToggle, false);
      }

      toggleDescription() {
        const isCollapsing = this.descriptionToggle.getAttribute('aria-expanded') === 'true';

        this.descriptionExpanded = !isCollapsing;
        this.description.classList.toggle('is-collapsed', !this.descriptionExpanded);
        this.updateToggle(this.descriptionToggle, this.descriptionExpanded);
        this.scheduleDescriptionRefresh();

        if (isCollapsing) {
          window.requestAnimationFrame(() => this.scrollToDescription());
        }
      }

      scrollToDescription() {
        this.alignDescriptionToViewport('auto');
      }

      alignDescriptionToViewport(behavior) {
        const descriptionTop = this.description.getBoundingClientRect().top;
        const desiredTop = this.getStickyHeaderBottom() + 20;
        const offset = descriptionTop - desiredTop;

        if (Math.abs(offset) < 2) return;

        window.scrollBy({ top: offset, behavior });
      }

      getStickyHeaderBottom() {
        const customHeader = document.querySelector('[data-gs-header]');
        const dawnHeader = document.querySelector('sticky-header, .header-wrapper');
        const header = customHeader?.closest('.shopify-section') || customHeader || dawnHeader;

        return Math.max(0, header?.getBoundingClientRect().bottom || 0);
      }

      setupFaqs() {
        if (!this.faqToggle || !this.extraFaqs.length) return;

        this.faqToggle.hidden = false;
        this.updateFaqVisibility();
      }

      setupDescriptionHeightObserver() {
        if (!this.specificationsColumn || !('ResizeObserver' in window)) return;

        this.descriptionHeightObserver = new ResizeObserver(() => this.scheduleDescriptionRefresh());
        this.descriptionHeightObserver.observe(this.specificationsColumn);
      }

      setupGalleryScrollIndicator() {
        if (!this.gallery || !this.galleryScrollIndicator || !this.galleryScrollThumb) return;

        this.galleryScrollController = new AbortController();
        const { signal } = this.galleryScrollController;
        const scheduleUpdate = () => {
          if (this.galleryScrollFrame) return;

          this.galleryScrollFrame = window.requestAnimationFrame(() => {
            this.galleryScrollFrame = null;
            this.updateGalleryScrollIndicator();
          });
        };
        const onPointerDown = (event) => {
          const indicatorRect = this.galleryScrollIndicator.getBoundingClientRect();
          const thumbRect = this.galleryScrollThumb.getBoundingClientRect();
          const maximumScroll = Math.max(0, this.gallery.scrollWidth - this.gallery.clientWidth);
          const maximumTravel = Math.max(0, indicatorRect.width - thumbRect.width);

          if (maximumScroll <= 0 || maximumTravel <= 0) return;

          event.preventDefault();

          if (event.clientX >= thumbRect.left && event.clientX <= thumbRect.right) {
            this.galleryScrollDragging = true;
            this.galleryScrollDragStartX = event.clientX;
            this.galleryScrollDragStartLeft = this.gallery.scrollLeft;
            this.galleryScrollDragMaximumScroll = maximumScroll;
            this.galleryScrollDragMaximumTravel = maximumTravel;
            this.gallery.classList.add('is-scrollbar-dragging');
            this.gallery.scrollTo({ left: this.gallery.scrollLeft, behavior: 'auto' });
            this.galleryScrollIndicator.classList.add('is-dragging');
            this.galleryScrollIndicator.setPointerCapture?.(event.pointerId);
            return;
          }

          const clickPosition = event.clientX - indicatorRect.left - thumbRect.width / 2;
          const progress = Math.min(1, Math.max(0, clickPosition / maximumTravel));
          this.gallery.scrollTo({
            left: progress * maximumScroll,
            behavior: 'smooth',
          });
        };
        const onPointerMove = (event) => {
          if (!this.galleryScrollDragging) return;

          event.preventDefault();
          const delta = event.clientX - this.galleryScrollDragStartX;
          const scrollLeft =
            this.galleryScrollDragStartLeft +
            delta * (this.galleryScrollDragMaximumScroll / this.galleryScrollDragMaximumTravel);
          this.gallery.scrollLeft = Math.min(
            this.galleryScrollDragMaximumScroll,
            Math.max(0, scrollLeft)
          );
        };
        const endPointerDrag = (event) => {
          if (!this.galleryScrollDragging) return;

          this.galleryScrollDragging = false;
          this.gallery.classList.remove('is-scrollbar-dragging');
          this.galleryScrollIndicator.classList.remove('is-dragging');
          if (this.galleryScrollIndicator.hasPointerCapture?.(event.pointerId)) {
            this.galleryScrollIndicator.releasePointerCapture(event.pointerId);
          }
        };
        const onKeyDown = (event) => {
          const maximumScroll = Math.max(0, this.gallery.scrollWidth - this.gallery.clientWidth);
          if (maximumScroll <= 0) return;

          let nextScrollLeft = null;
          if (event.key === 'ArrowLeft') {
            nextScrollLeft = this.gallery.scrollLeft - this.gallery.clientWidth * 0.8;
          }
          if (event.key === 'ArrowRight') {
            nextScrollLeft = this.gallery.scrollLeft + this.gallery.clientWidth * 0.8;
          }
          if (event.key === 'Home') nextScrollLeft = 0;
          if (event.key === 'End') nextScrollLeft = maximumScroll;

          if (nextScrollLeft === null) return;

          event.preventDefault();
          this.gallery.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
        };

        this.galleryScrollDragging = false;
        this.galleryScrollDragStartX = 0;
        this.galleryScrollDragStartLeft = 0;
        this.galleryScrollDragMaximumScroll = 0;
        this.galleryScrollDragMaximumTravel = 0;

        this.gallery.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
        window.addEventListener('resize', scheduleUpdate, { passive: true, signal });
        this.galleryScrollIndicator.addEventListener('pointerdown', onPointerDown, { signal });
        window.addEventListener('pointermove', onPointerMove, { passive: false, signal });
        window.addEventListener('pointerup', endPointerDrag, { signal });
        window.addEventListener('pointercancel', endPointerDrag, { signal });
        this.galleryScrollIndicator.addEventListener('keydown', onKeyDown, { signal });

        if ('ResizeObserver' in window) {
          this.galleryScrollObserver = new ResizeObserver(scheduleUpdate);
          this.galleryScrollObserver.observe(this.gallery);
          this.galleryScrollObserver.observe(this.galleryScrollIndicator);
        }

        scheduleUpdate();
      }

      updateGalleryScrollIndicator() {
        if (!this.gallery || !this.galleryScrollIndicator || !this.galleryScrollThumb) return;

        const scrollWidth = this.gallery.scrollWidth;
        const visibleWidth = this.gallery.clientWidth;
        const trackWidth = this.galleryScrollIndicator.clientWidth;
        const maximumScroll = Math.max(0, scrollWidth - visibleWidth);
        const visibleRatio = scrollWidth > 0 ? Math.min(1, visibleWidth / scrollWidth) : 1;
        const thumbWidth = Math.min(trackWidth, Math.max(24, trackWidth * visibleRatio));
        const maximumTravel = Math.max(0, trackWidth - thumbWidth);
        const progress = maximumScroll > 0 ? Math.min(1, Math.max(0, this.gallery.scrollLeft / maximumScroll)) : 0;

        this.galleryScrollThumb.style.width = `${thumbWidth}px`;
        this.galleryScrollThumb.style.transform = `translateX(${maximumTravel * progress}px)`;
        const isScrollable = maximumScroll > 1;
        this.galleryScrollIndicator.classList.toggle('is-hidden', !isScrollable);
        this.galleryScrollIndicator.setAttribute('aria-hidden', String(!isScrollable));
        this.galleryScrollIndicator.setAttribute('tabindex', isScrollable ? '0' : '-1');
        this.galleryScrollIndicator.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
        this.galleryScrollIndicator.setAttribute(
          'aria-valuetext',
          `${Math.round(progress * 100)}% scrolled`
        );
      }

      toggleFaqs() {
        this.faqExpanded = !this.faqExpanded;
        this.updateFaqVisibility();
      }

      updateFaqVisibility() {
        this.extraFaqs.forEach((faq) => {
          faq.hidden = !this.faqExpanded;
          if (!this.faqExpanded) {
            faq.removeAttribute('open');
            faq.querySelector('summary')?.setAttribute('aria-expanded', 'false');
          }
        });
        this.updateToggle(this.faqToggle, this.faqExpanded);
      }

      updateToggle(toggle, isExpanded) {
        if (!toggle) return;

        toggle.setAttribute('aria-expanded', String(isExpanded));
        const label = isExpanded ? toggle.dataset.showLessLabel : toggle.dataset.showMoreLabel;
        const labelElement = toggle.querySelector('span');
        if (labelElement) labelElement.textContent = label;
      }
    }
  );
}
